import { useEffect, useRef, ReactNode } from 'react';

interface FocusTrapProps {
  children: ReactNode;
  active?: boolean;
  initialFocus?: HTMLElement | null;
  onEscape?: () => void;
}

export function FocusTrap({ children, active = true, initialFocus, onEscape }: FocusTrapProps) {
  const containerRef = useRef<HTMLDivElement>(null);
  const previousActiveElement = useRef<HTMLElement | null>(null);

  useEffect(() => {
    if (!active) return;

    const container = containerRef.current;
    if (!container) return;

    // Save currently focused element
    previousActiveElement.current = document.activeElement as HTMLElement;

    // Focus initial element or first focusable element
    const focusableElements = getFocusableElements(container);
    if (initialFocus && container.contains(initialFocus)) {
      initialFocus.focus();
    } else if (focusableElements.length > 0) {
      focusableElements[0].focus();
    }

    const handleKeyDown = (e: KeyboardEvent) => {
      if (e.key === 'Escape' && onEscape) {
        e.preventDefault();
        onEscape();
        return;
      }

      if (e.key !== 'Tab') return;

      const focusableElements = getFocusableElements(container);
      const firstElement = focusableElements[0];
      const lastElement = focusableElements[focusableElements.length - 1];

      if (e.shiftKey) {
        // Shift + Tab
        if (document.activeElement === firstElement) {
          e.preventDefault();
          lastElement?.focus();
        }
      } else {
        // Tab
        if (document.activeElement === lastElement) {
          e.preventDefault();
          firstElement?.focus();
        }
      }
    };

    document.addEventListener('keydown', handleKeyDown);

    return () => {
      document.removeEventListener('keydown', handleKeyDown);
      
      // Restore focus when trap is removed
      if (previousActiveElement.current && document.body.contains(previousActiveElement.current)) {
        previousActiveElement.current.focus();
      }
    };
  }, [active, initialFocus, onEscape]);

  return (
    <div ref={containerRef} className="focus-trap">
      {children}
    </div>
  );
}

function getFocusableElements(container: HTMLElement): HTMLElement[] {
  const selector = [
    'a[href]',
    'area[href]',
    'input:not([disabled])',
    'select:not([disabled])',
    'textarea:not([disabled])',
    'button:not([disabled])',
    'iframe',
    'object',
    'embed',
    '[contenteditable]',
    '[tabindex]:not([tabindex^="-"])',
  ].join(',');

  return Array.from(container.querySelectorAll<HTMLElement>(selector)).filter(
    (element) => {
      // Filter out elements that are not visible.
      //
      // Resolve the view from the element's OWN document rather than assuming
      // this window. The selector matches iframe/object/embed, so a node can
      // belong to another realm; Safari's binding check then rejects it with
      // "Argument 1 ('element') to Window.getComputedStyle must be an instance
      // of Element", while Chrome is laxer and this never surfaces there
      // (Sentry LEAFNODE-WEB-3: 100% Mobile Safari).
      //
      // It matters because this runs inside the keydown handler: one bad node
      // threw the whole handler, so Tab-cycling died for that keypress instead
      // of skipping one element. The try/catch keeps that guarantee for any
      // cause we have not thought of — an untabbable element is a far better
      // outcome than a dead focus trap.
      try {
        const view = element.ownerDocument?.defaultView ?? window;
        const style = view.getComputedStyle(element);
        return (
          style.display !== 'none' &&
          style.visibility !== 'hidden' &&
          element.offsetParent !== null
        );
      } catch {
        return false;
      }
    }
  );
}
